import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { articles } from '@/mocks/articles'
import { categories } from '@/mocks/categories'
import ArticleCard from '@/components/feature/ArticleCard'
import Breadcrumb from '@/components/feature/Breadcrumb'
import SEOHead from '@/components/feature/SEOHead'

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>()
  const { t } = useTranslation('common')
  const currentSlug = slug || ''

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentSlug])

  const currentCategory = categories.find((c) => c.slug === currentSlug)
  const showAll = currentSlug === 'all' || !currentCategory
  const filteredArticles = showAll ? articles : articles.filter((a) => a.category === currentSlug)

  const pageTitle = showAll ? t('category.title') : currentCategory?.name || t('category.title')
  const pageDesc = showAll
    ? t('category.subtitle')
    : `${filteredArticles.length} 个精彩视频`

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${pageTitle} - 快猫短视频`,
    description: `浏览快猫短视频${pageTitle}下的精彩短视频内容`,
    url: `https://kuaikuaimao.com/category/${currentSlug}`,
    isPartOf: { '@type': 'WebSite', name: '快猫短视频' },
  }

  return (
    <>
      <SEOHead
        title={`${pageTitle}分类`}
        description={`浏览快猫短视频${pageTitle}下的精彩短视频内容，发现更多有趣视频`}
        keywords={`快猫短视频,${currentCategory?.name || ''},短视频分类,视频推荐`}
        canonicalUrl={`https://kuaikuaimao.com/category/${currentSlug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <main className="pt-20 md:pt-24">
        <div className="mx-auto px-4 md:px-6 max-w-7xl">
          <Breadcrumb
            items={[
              { label: t('breadcrumb.home'), path: '/' },
              { label: pageTitle },
            ]}
          />

          <div className="mb-8 md:mb-12">
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-2">
              {pageTitle}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base">
              {pageDesc}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
            <Link
              to="/category/all"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                showAll
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {t('category.allCategories')}
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 pb-16 md:pb-24">
              {filteredArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  id={article.id}
                  title={article.title}
                  description={article.description}
                  thumbnail={article.thumbnail}
                  category={article.category}
                  categoryName={categories.find((c) => c.slug === article.category)?.name || article.category}
                  author={article.author}
                  views={article.views}
                  likes={article.likes}
                  date={article.date}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-foreground-500">
              <i className="ri-inbox-line text-5xl block mb-4"></i>
              <p className="text-lg">{t('category.noArticles')}</p>
              <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block whitespace-nowrap">
                {t('category.backToHome')}
              </Link>
            </div>
          )}
        </div>
      </main>
    </>
  )
}